import memoize from 'lru-memoize';
import React, {FC, ReactNode, useEffect, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownInheritableProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {ChannelDropdownItem} from './channelDropdownItem';

/*
 * Props.
 */

interface CustomerPortalChannelPickerProps {
  channel?: ChannelModel;
  availableChannels: ReadonlyArray<ChannelModel>;
  onChannelSelected: (channel: ChannelModel) => void;
}

/*
 * Component.
 */

const CustomerPortalChannelPicker: FC<CustomerPortalChannelPickerProps & DropdownInheritableProps> = (
  props,
) => {
  const {availableChannels} = props;
  const [query, setQuery] = useState('');

  const filteredData = filterRows(availableChannels, query);
  const renderRow = (channel: ChannelModel, index: number): ReactNode => (
    <ChannelDropdownItem
      key={channel.id}
      index={index}
      currentChannel={props.channel}
      channel={channel}
      onSelect={() => props.onChannelSelected(channel)}
    />
  );

  return (
    <SearchDropdown<ChannelModel>
      {...props}
      data={filteredData}
      query={query}
      onQueryChange={setQuery}
      renderRow={renderRow}
      width={260}
      headerTitle={<Trans id="customer-portal-picker-channels">Channels</Trans>}
    />
  );
};

// Wrap the component with the DropdownBoxCoordinator to handle the dropdown state.
export const CustomerPortalChannelPickerWrapped: FC<CustomerPortalChannelPickerProps> = (props) => {
  const {channel, availableChannels, onChannelSelected} = props;

  useEffect(() => {
    if (!channel && availableChannels.length > 0) {
      onChannelSelected(availableChannels[0]);
    }
  }, [availableChannels, channel, onChannelSelected]);

  return (
    <DropdownBoxCoordinator
      label={<Trans id="customer-portal-picker-channel">Customer portal</Trans>}
      value={props.channel?.name || <Trans id="customer-portal-picker-select-channel">Select channel</Trans>}
      renderDropdown={(popoverProps) => <CustomerPortalChannelPicker {...popoverProps} {...props} />}
    />
  );
};

/*
 * Helpers.
 */

const filterRows = memoize(20)((channels: ReadonlyArray<ChannelModel>, query: string) => {
  // Shortcut for empty query.
  if (!query) {
    return channels;
  }

  // Otherwise, filter by name.
  const lowerCaseQuery = query.toLowerCase();
  return channels.filter((c) => c.displayName.toLowerCase().includes(lowerCaseQuery));
});
